<template>
  <BaseSimuladorIndicadorModal
    ref="baseModal"
    :modal-id="modalId"
    title="Simulador"
    numerador-label="Numerador"
    denominador-label="Denominador"
    :valor-numerador="nManual"
    :valor-denominador="dManual"
    :valor-resultado="valorResultado"
    :base-numerador="baseNumerador"
    :base-denominador="baseDenominador"
    :procedimentos-numerador="PROCEDIMENTOS_NUMERADOR"
    :procedimentos-denominador="PROCEDIMENTOS_DENOMINADOR"
    :tipos="TIPOS_CLASSIFICACAO"
    :calculate-result-fn="calcularResultado"
    :calculate-class-fn="calcularClasse"
    :apply-preset-fn="aplicarPreset"
    :format-delta-fn="formatDelta"
    :result-text-fn="montarTextoResultado"
    hint="💡 Os botões preenchem valores de exemplo. Ajuste manualmente pelos controles acima."
    @update:numerador="setNumerador"
    @update:denominador="setDenominador"
    :tipoResultado="tipoResultado"
  />
</template>

<script>
import BaseSimuladorIndicadorModal from '@/pages/IndicadoresAps/components/BaseSimuladorIndicadorModal.vue'

export default {
  name: 'SimuladorModal',
  components: {
    BaseSimuladorIndicadorModal,
  },
  props: {
    modalId: {
      type: String,
      default: 'simulador-modal',
    },
    dadosPercentualMunicipio: {
      type: Object,
      default: () => ({}),
    },
    dadosPercentualEquipe: {
      type: Object,
      default: () => ({}),
    },
    dadosPercentualMicroArea: {
      type: Object,
      default: () => ({}),
    },
    selectedEquipe: {
      type: [Object, String],
      default: '',
    },
    selectedMicroArea: {
      type: String,
      default: '',
    },
    tipoResultado: {
      type: String,
      default: 'quadrimestral',
    },
  },
  data() {
    return {
      nManual: 0,
      dManual: 1,
      valorResultado: 0,
      baseNumerador: 0,
      baseDenominador: 1,
      TIPOS_CLASSIFICACAO: [
        { value: 'Ótimo', label: 'Ótimo', description: '> 50 e <= 70' },
        { value: 'Bom', label: 'Bom', description: '> 30 e <= 50' },
        {
          value: 'Suficiente',
          label: 'Suficiente',
          description: '> 10 e <= 30',
        },
        { value: 'Regular', label: 'Regular', description: '<= 10 ou > 70' },
      ],
      PROCEDIMENTOS_NUMERADOR: [
        {
          icon: null,
          codigo: null,
          desc: 'Atendimentos por demanda programada (consulta agendada programada; cuidado continuado; e consulta agendada) realizados por Médicos e Enfermeiros.',
        },
      ],
      PROCEDIMENTOS_DENOMINADOR: [
        {
          icon: null,
          codigo: null,
          desc: 'Total de atendimentos por todos os tipos de demandas (espontâneas e programadas) realizados por Médicos e Enfermeiros.',
        },
      ],
    }
  },
  methods: {
    /**
     * Abre o modal e aplica a fonte de dados inicial
     * @returns {void}
     */
    open() {
      this.aplicarFonte()
      this.$refs.baseModal.open()
    },

    /**
     * Fecha o modal base referenciado no componente
     * @returns {void}
     */
    close() {
      this.$refs.baseModal.close()
    },

    /**
     * Define a fonte de dados atual com base nos filtros selecionados e atualiza os valores manuais
     * @returns {void}
     */
    aplicarFonte() {
      let fonte = this.dadosPercentualMunicipio

      if (this.selectedMicroArea) {
        fonte = this.dadosPercentualMicroArea
      } else if (this.selectedEquipe) {
        fonte = this.dadosPercentualEquipe
      }

      const numerador = Number(fonte?.numerador) || 0
      const denominador = Number(fonte?.denominador) || 1
      const resultado = Number(fonte?.resultado) || 0

      this.nManual = numerador
      this.dManual = denominador
      // No quadrimestral, o valor inicial vem da fonte.
      this.valorResultado = resultado
      this.resetBase()
    },

    /**
     * Define manualmente o valor do numerador
     * @param {number} valor
     * @returns {void}
     */
    setNumerador(valor) {
      this.nManual = valor
      this.valorResultado = this.calcularResultado({
        numerador: this.nManual,
        denominador: this.dManual,
      })
    },

    /**
     * Define manualmente o valor do denominador
     * @param {number} valor
     * @returns {void}
     */
    setDenominador(valor) {
      this.dManual = valor
      this.valorResultado = this.calcularResultado({
        numerador: this.nManual,
        denominador: this.dManual,
      })
    },

    /**
     * Reseta os valores base do numerador e denominador com os valores atuais
     * @returns {void}
     */
    resetBase() {
      this.baseNumerador = this.nManual
      this.baseDenominador = this.dManual
    },

    /**
     * Calcula o resultado com base no numerador e denominador informados
     * @param {{ numerador: number|string, denominador: number|string }} params
     * @returns {number}
     */
    calcularResultado({ numerador, denominador }) {
      const d = Number(denominador) || 0
      if (d <= 0) return 0

      const n = Number(numerador) || 0
      return Number(((n / d) * 100).toFixed(2))
    },

    /**
     * Calcula a classe do resultado, retornando nível e variante visual
     * @param {{ resultado: number }} params
     * @returns {{ nivel: string, variant: string }}
     */
    calcularClasse({ resultado }) {
      if (resultado > 50 && resultado <= 70) {
        return { nivel: 'Ótimo', variant: 'primary' }
      }
      if (resultado > 30 && resultado <= 50) {
        return { nivel: 'Bom', variant: 'success' }
      }
      if (resultado > 10 && resultado <= 30) {
        return { nivel: 'Suficiente', variant: 'warning' }
      }
      if (resultado <= 10 || resultado > 70)
        return { nivel: 'Regular', variant: 'danger' }

      return { nivel: 'Regular', variant: 'danger' }
    },

    /**
     * Aplica um preset de classificação ajustando numerador e denominador
     * @param {{
     *   tipo: string,
     *   setNumerador: (valor: number) => void,
     *   setDenominador: (valor: number) => void
     * }} params
     * @returns {void}
     */
    aplicarPreset({ tipo, setNumerador, setDenominador }) {
      if (tipo === 'Ótimo') {
        setNumerador(51)
        setDenominador(100)
      } else if (tipo === 'Bom') {
        setNumerador(31)
        setDenominador(100)
      } else if (tipo === 'Suficiente') {
        setNumerador(11)
        setDenominador(100)
      } else {
        setNumerador(10)
        setDenominador(100)
      }

      this.$nextTick(() => {
        this.resetBase()
      })
    },

    /**
     * Formata o delta numérico para exibição com sinal
     * @param {number} val
     * @returns {string}
     */
    formatDelta(val) {
      if (val > 0) return `+${val}`
      if (val < 0) return `${val}`
      return ''
    },

    /**
     * Monta o texto de resultado para exibição
     * @param {{ resultado: number }} params
     * @returns {string}
     */
    montarTextoResultado({ resultado }) {
      return `NM / DN × 100`
    },
  },
}
</script>
